import React, { useState, useMemo, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  Star, Truck, RefreshCw, ShieldCheck, Check, Plus, Minus,
  ChevronLeft, Ruler, Heart, Share2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import JoinClub from "@/components/site/JoinClub";
import CartDrawer from "@/components/site/CartDrawer";
import SearchOverlay from "@/components/site/SearchOverlay";
import ProductImage from "@/components/ProductImage";
import { products, productDetails, productFeatures } from "@/mock";
import { useCart } from "@/context/CartContext";
import { toast } from "@/hooks/use-toast";
import * as Icons from "lucide-react";

export default function ProductPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { add } = useCart();
  const [cartOpen, setCartOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  const product = useMemo(
    () => products.find((p) => p.id === id) || products.find((p) => p.slug === id),
    [id]
  );

  const [size, setSize] = useState("M");
  const [qty, setQty] = useState(1);
  const [galleryIdx, setGalleryIdx] = useState(0);

  useEffect(() => {
    if (product) {
      const ownIdx = productDetails.gallery.findIndex((g) => g === product.img);
      setGalleryIdx(ownIdx >= 0 ? ownIdx : 0);
      window.scrollTo({ top: 0, behavior: "instant" });
    }
  }, [product]);

  if (!product) {
    return (
      <div className="bg-white min-h-screen">
        <Header onOpenCart={() => setCartOpen(true)} onOpenSearch={() => setSearchOpen(true)} />
        <div className="max-w-3xl mx-auto px-6 py-24 text-center">
          <h1 className="text-3xl font-black tracking-tight">Product not found</h1>
          <p className="text-black/60 mt-3">The product you’re looking for doesn’t exist.</p>
          <Button
            onClick={() => navigate("/")}
            className="mt-6 bg-[#c60d0d] hover:bg-[#e11d1d] text-white rounded-none px-6 h-11 tracking-[0.16em] text-[12px] font-bold uppercase"
          >
            Back to Home
          </Button>
        </div>
        <Footer />
      </div>
    );
  }

  const handleAdd = () => {
    for (let i = 0; i < qty; i++) add({ ...product, size });
    toast({
      title: "Added to cart",
      description: `${product.color} · Size ${size} · Qty ${qty}`,
    });
    setCartOpen(true);
  };

  const related = products.filter((p) => p.id !== product.id);

  return (
    <div className="bg-white min-h-screen">
      <Header onOpenCart={() => setCartOpen(true)} onOpenSearch={() => setSearchOpen(true)} />

      {/* Breadcrumb */}
      <div className="border-b border-black/10 bg-white">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-3 flex items-center gap-2 text-[11px] tracking-[0.16em] uppercase text-black/50">
          <Link to="/" className="hover:text-black flex items-center gap-1">
            <ChevronLeft size={12} /> Home
          </Link>
          <span>/</span>
          <Link to="/#shop" className="hover:text-black">Shop</Link>
          <span>/</span>
          <span className="text-black">{product.color}</span>
        </div>
      </div>

      {/* Main product section */}
      <section className="bg-white py-8 md:py-14">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 grid md:grid-cols-2 gap-8 md:gap-14">
          {/* Gallery */}
          <div>
            <div className="relative bg-[#0a0a0a] aspect-square flex items-center justify-center overflow-hidden">
              {product.tag && (
                <span
                  className={`absolute top-4 left-4 z-10 text-[10px] tracking-[0.22em] font-black px-2.5 py-1.5 ${
                    product.sale ? "bg-[#c60d0d] text-white" : "bg-white text-black"
                  }`}
                >
                  {product.tag}
                </span>
              )}
              <div className="absolute inset-8 md:inset-14 flex items-center justify-center">
                <ProductImage
                  src={productDetails.gallery[galleryIdx] || product.img}
                  alt={product.color}
                  className="drop-shadow-[0_40px_50px_rgba(0,0,0,0.55)]"
                />
              </div>
            </div>
            {/* Thumbnails */}
            <div className="mt-4 grid grid-cols-4 gap-3">
              {productDetails.gallery.map((g, i) => (
                <button
                  key={i}
                  onClick={() => setGalleryIdx(i)}
                  className={`relative aspect-square bg-[#0a0a0a] border-2 transition ${
                    galleryIdx === i ? "border-[#c60d0d]" : "border-transparent hover:border-black/40"
                  }`}
                >
                  <div className="absolute inset-3 flex items-center justify-center">
                    <ProductImage src={g} alt={`View ${i + 1}`} />
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Details */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <div className="flex text-[#c60d0d]">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={14} fill="#c60d0d" strokeWidth={0} />
                ))}
              </div>
              <span className="text-[11px] tracking-[0.16em] uppercase text-black/50">
                4.9 · 12,438 reviews
              </span>
            </div>

            <h1 className="text-3xl md:text-4xl font-black tracking-tight text-black leading-[1.05]">
              {product.name}
            </h1>
            <div className="mt-1 text-[12px] tracking-[0.22em] uppercase text-black/50">
              Color: <span className="text-black ml-1 font-bold">{product.color}</span>
            </div>

            <div className="mt-5 flex items-baseline gap-3">
              <span className={`text-3xl font-black ${product.sale ? "text-[#c60d0d]" : "text-black"}`}>
                ${product.price}
              </span>
              {product.compareAt && (
                <span className="text-black/40 line-through text-lg">${product.compareAt}</span>
              )}
              {product.sale && (
                <span className="bg-[#c60d0d] text-white text-[10px] tracking-[0.18em] font-black uppercase px-2 py-1">
                  Save ${product.compareAt - product.price}
                </span>
              )}
            </div>

            {/* Color options */}
            <div className="mt-8">
              <div className="flex items-center justify-between mb-3">
                <div className="text-[11px] tracking-[0.22em] uppercase text-black/60 font-bold">
                  Color <span className="text-black ml-2">{product.color}</span>
                </div>
              </div>
              <div className="flex items-center gap-3">
                {products.map((p) => (
                  <Link
                    key={p.id}
                    to={`/products/${p.id}`}
                    aria-label={p.color}
                    className={`h-11 w-11 rounded-full border-2 transition-all ${
                      p.id === product.id
                        ? "border-[#c60d0d] scale-110"
                        : "border-black/20 hover:border-black/60"
                    }`}
                    style={{ backgroundColor: p.swatch }}
                  />
                ))}
              </div>
            </div>

            {/* Size selector */}
            <div className="mt-8">
              <div className="flex items-center justify-between mb-3">
                <div className="text-[11px] tracking-[0.22em] uppercase text-black/60 font-bold">
                  Size <span className="text-black ml-2">{size}</span>
                </div>
                <a
                  href="/#sizing"
                  className="inline-flex items-center gap-1 text-[11px] tracking-[0.18em] uppercase text-black/60 hover:text-[#c60d0d]"
                >
                  <Ruler size={12} /> Size Guide
                </a>
              </div>
              <div className="grid grid-cols-5 gap-2">
                {productDetails.sizes.map((s) => (
                  <button
                    key={s}
                    onClick={() => setSize(s)}
                    className={`py-3 text-[13px] font-black tracking-[0.08em] border-2 transition-all ${
                      size === s
                        ? "bg-black text-white border-black"
                        : "bg-white text-black border-black/15 hover:border-black/60"
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>

            {/* Quantity + Add */}
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <div className="flex items-center border-2 border-black/15 h-12">
                <button
                  onClick={() => setQty(Math.max(1, qty - 1))}
                  className="px-4 h-full hover:bg-black/5"
                  aria-label="Decrease quantity"
                >
                  <Minus size={14} />
                </button>
                <span className="w-10 text-center font-black text-[15px]">{qty}</span>
                <button
                  onClick={() => setQty(qty + 1)}
                  className="px-4 h-full hover:bg-black/5"
                  aria-label="Increase quantity"
                >
                  <Plus size={14} />
                </button>
              </div>
              <Button
                onClick={handleAdd}
                className="flex-1 bg-[#c60d0d] hover:bg-[#e11d1d] text-white rounded-none h-12 tracking-[0.18em] text-[13px] font-black uppercase"
              >
                Add to Cart · ${product.price * qty}
              </Button>
              <button
                aria-label="Save"
                className="h-12 w-12 border-2 border-black/15 hover:border-[#c60d0d] hover:text-[#c60d0d] flex items-center justify-center transition"
              >
                <Heart size={18} />
              </button>
            </div>

            {/* Trust icons */}
            <div className="mt-8 grid grid-cols-3 gap-3">
              <div className="flex items-center gap-2 text-[11px] tracking-[0.12em] uppercase text-black/70">
                <Truck size={16} className="text-[#c60d0d]" />
                <span>{productDetails.shipping.ships}</span>
              </div>
              <div className="flex items-center gap-2 text-[11px] tracking-[0.12em] uppercase text-black/70">
                <RefreshCw size={16} className="text-[#c60d0d]" />
                <span>{productDetails.shipping.returns}</span>
              </div>
              <div className="flex items-center gap-2 text-[11px] tracking-[0.12em] uppercase text-black/70">
                <ShieldCheck size={16} className="text-[#c60d0d]" />
                <span>{productDetails.shipping.packaging}</span>
              </div>
            </div>

            {/* Feature bullets */}
            <div className="mt-8 border-t border-black/10 pt-6">
              <div className="text-[11px] tracking-[0.22em] uppercase text-black/60 font-bold mb-3">
                Built for comfort
              </div>
              <ul className="space-y-2">
                {productDetails.bullets.map((b) => (
                  <li key={b} className="flex items-start gap-2 text-[13px] text-black/80">
                    <Check size={14} className="text-[#c60d0d] mt-1 shrink-0" strokeWidth={3} />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Accordions */}
            <Accordion type="single" collapsible className="mt-8 border-t border-black/10">
              <AccordionItem value="desc" className="border-b border-black/10">
                <AccordionTrigger className="text-[12px] tracking-[0.18em] uppercase font-black hover:no-underline">
                  Description
                </AccordionTrigger>
                <AccordionContent className="text-[13px] text-black/70 leading-relaxed">
                  {productDetails.description}
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="care" className="border-b border-black/10">
                <AccordionTrigger className="text-[12px] tracking-[0.18em] uppercase font-black hover:no-underline">
                  Fabric & Care
                </AccordionTrigger>
                <AccordionContent className="text-[13px] text-black/70 leading-relaxed">
                  95% combed cotton, 5% spandex. Machine wash cold with like colors. Tumble dry low.
                  Do not bleach. Made in Pakistan.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="ship" className="border-b border-black/10">
                <AccordionTrigger className="text-[12px] tracking-[0.18em] uppercase font-black hover:no-underline">
                  Shipping & Returns
                </AccordionTrigger>
                <AccordionContent className="text-[13px] text-black/70 leading-relaxed">
                  Free US shipping on orders over $75. Discreet, unbranded packaging. 30-day easy
                  returns — first exchange is on us.
                </AccordionContent>
              </AccordionItem>
            </Accordion>

            <button className="mt-6 inline-flex items-center gap-2 text-[11px] tracking-[0.18em] uppercase text-black/60 hover:text-[#c60d0d]">
              <Share2 size={12} /> Share
            </button>
          </div>
        </div>
      </section>

      {/* Feature icon row */}
      <section className="bg-[#f4f4f2] border-y border-black/10">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-8 grid grid-cols-2 md:grid-cols-5 gap-6">
          {productFeatures.map((f) => {
            const Icon = Icons[f.icon] || Icons.Check;
            return (
              <div key={f.label} className="flex items-center gap-3">
                <div className="h-11 w-11 rounded-full bg-white border border-black/10 flex items-center justify-center shrink-0 shadow-sm">
                  <Icon size={18} className="text-[#c60d0d]" strokeWidth={2.4} />
                </div>
                <div className="text-[11px] tracking-[0.16em] font-black uppercase whitespace-pre-line">
                  {f.label}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Related products */}
      <section className="bg-white py-16 md:py-20">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <div className="flex items-end justify-between mb-8">
            <div>
              <div className="text-[11px] tracking-[0.28em] text-[#c60d0d] font-bold uppercase mb-2">
                You might also like
              </div>
              <h2 className="text-3xl md:text-4xl font-black tracking-tight">
                COMPLETE THE DRAWER
              </h2>
            </div>
            <Link
              to="/#shop"
              className="hidden md:inline-flex items-center gap-2 text-[12px] font-bold tracking-[0.18em] uppercase text-black/70 hover:text-[#c60d0d]"
            >
              Shop All
            </Link>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
            {related.map((p) => (
              <Link key={p.id} to={`/products/${p.id}`} className="group block">
                <div className="relative aspect-square bg-[#0a0a0a] overflow-hidden">
                  {p.tag && (
                    <span
                      className={`absolute top-3 right-3 z-10 text-[10px] tracking-[0.2em] font-black px-2 py-1 ${
                        p.sale ? "bg-[#c60d0d] text-white" : "bg-white text-black"
                      }`}
                    >
                      {p.tag}
                    </span>
                  )}
                  <div className="absolute inset-8 flex items-center justify-center transition-transform duration-500 group-hover:scale-[1.05]">
                    <ProductImage src={p.img} alt={p.color} />
                  </div>
                </div>
                <div className="mt-3 flex items-center justify-between">
                  <div>
                    <div className="text-[10px] tracking-[0.22em] uppercase text-black/50">{p.color}</div>
                    <div className="text-[13px] font-bold text-black mt-0.5 group-hover:text-[#c60d0d]">
                      {p.name}
                    </div>
                  </div>
                  <div className={`text-[15px] font-black ${p.sale ? "text-[#c60d0d]" : "text-black"}`}>
                    ${p.price}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <JoinClub />
      <Footer />
      <CartDrawer open={cartOpen} onClose={() => setCartOpen(false)} />
      <SearchOverlay open={searchOpen} onClose={() => setSearchOpen(false)} />
    </div>
  );
}
